import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore, hasPermission } from '@/store/auth.store';
import { useThemeStore } from '@/store/theme.store';
import { useIncidentStore } from '@/store/incident.store';
import { useBrandingStore } from '@/store/branding.store';
import { useIsMobile, useIsDesktop } from '@/hooks/useMediaQuery';
import { useKeyboardShortcutsModal } from '@/hooks/useKeyboardShortcutsModal';
import { useMyCurrentPosition } from '@/hooks/usePositions';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMenuKeyboardNav } from '@/hooks/useMenuKeyboardNav';
import { endEmbeddedConsoleSession } from '@/services/embedded-console-session';
import { revokeConsoleSession } from '@/services/session-refresh';
import { isEmbeddedMode } from '@/utils/embedded-console';
import TenantSwitcher from '../tenants/TenantSwitcher';
import IncidentSwitcher from '../incidents/IncidentSwitcher';
import FaIcon from '../common/FaIcon';
import Avatar from '../common/Avatar';

import SwitchPositionModal from './SwitchPositionModal';
import { AARIcon, ActivityLogIcon, AdminIcon, ArrowPathIcon, AlertIcon, AtRiskIcon, AuditIcon, AutomationIcon, BellAlertIcon, BoardIcon, BoxOpenIcon, BuildingOfficeIcon, BusinessIcon, ChartBarIcon, ChartLineIcon, ChevronDownIcon, ClipboardDocumentCheckIcon, ClipboardDocumentListIcon, ClockIcon, CloudIcon, ComputerIcon, ContactsIcon, CoopIcon, CriticalInfraIcon, DamageAssessmentIcon, DebrisIcon, DocumentTextIcon, DroneIcon, EllipsisHorizontalIcon, EvacuationIcon, EyeIcon, FieldOpsIcon, FileInvoiceDollarIcon, FileLibraryIcon, FireIcon, FormIcon, HandshakeIcon, HazmatIcon, HeadsetIcon, HelpIcon, HomeIcon, InventoryIcon, KeyboardIcon, LifelinesIcon, LogoutIcon, MapIcon, MoonIcon, NotificationIcon, PIOIcon, ResourcesIcon, RoadIcon, SettingsIcon, ShelterIcon, SignalIcon, SunIcon, TaskIcon, TruckIcon, UserGroupIcon, UtilityIcon, VideoCameraIcon, WeatherIcon } from '../icons';

// =============================================================================
// ICONS — thin wrappers around FaIcon for NavItem.icon compatibility
// =============================================================================

function CredentialIcon({ className }: { className?: string }) {
  return <FaIcon icon="address-card" className={className} />;
}

function FloodOpsIcon({ className }: { className?: string }) {
  return <FaIcon icon="house-flood-water" className={className} />;
}

function TropicalIcon({ className }: { className?: string }) {
  return <FaIcon icon="hurricane" className={className} />;
}

function CommandCenterIcon({ className }: { className?: string }) {
  return <FaIcon icon="satellite-dish" className={className} />;
}

function MissionIcon({ className }: { className?: string }) {
  return <FaIcon icon="route" className={className} />;
}

function ExerciseIcon({ className }: { className?: string }) {
  return <FaIcon icon="flask" className={className} />;
}

// =============================================================================
// NAVIGATION STRUCTURE
// =============================================================================

type IconComponent = ({ className }: { className?: string }) => JSX.Element;

interface NavItem {
  name: string;
  href: string;
  icon: IconComponent;
  requiresIncident?: boolean; // If true, hidden in Daily Operations mode
  mobileOnly?: boolean; // If true, hidden on desktop-sized viewports
  subGroup?: string; // Sub-group label for visual dividers within a group
  permission?: { module: string; action: string }; // Permission required to see this item
  featureFlag?: string; // Plan entitlement required to see this item (hidden if plan lacks it)
}

interface NavGroup {
  name: string;
  icon: IconComponent;
  items: NavItem[];
  defaultOpen?: boolean;
  requiresIncident?: boolean; // If true, entire group hidden in Daily Operations mode
}

// Top-level items (always visible)
const topNavigation: NavItem[] = [
  { name: 'Overview', href: '/', icon: HomeIcon },
  { name: 'Map', href: '/map', icon: MapIcon },
  { name: 'Field Ops', href: '/field', icon: FieldOpsIcon, requiresIncident: true, mobileOnly: true },
];

// Grouped navigation
// Items marked with requiresIncident: true are hidden in Daily Operations mode
const navGroups: NavGroup[] = [
  {
    name: 'Response',
    icon: AlertIcon,
    defaultOpen: true,
    items: [
      { name: 'Incidents', href: '/incidents', icon: AlertIcon, permission: { module: 'incidents', action: 'view' } },
      { name: 'Call Center', href: '/call-center', icon: HeadsetIcon, requiresIncident: true, permission: { module: 'call_center', action: 'view' } },
      { name: 'Tasks', href: '/tasks', icon: TaskIcon, requiresIncident: true, permission: { module: 'tasks', action: 'view' } },
      { name: 'Organization', href: '/incident-org', icon: UserGroupIcon, requiresIncident: true, permission: { module: 'incident_org', action: 'view' } },
      { name: 'Forms', href: '/forms', icon: FormIcon, requiresIncident: true, permission: { module: 'forms', action: 'view' } },
      { name: 'Activity Logs', href: '/ics214', icon: ActivityLogIcon, requiresIncident: true },
      { name: 'Refused Field Changes', href: '/field-changes/refused', icon: ArrowPathIcon, permission: { module: 'sync_rejections', action: 'view' } },
      { name: 'App Upgrade Readiness', href: '/field-changes/app-upgrade', icon: ArrowPathIcon, permission: { module: 'sync_rejections', action: 'view' } },
      { name: 'Board', href: '/board', icon: BoardIcon, requiresIncident: true, permission: { module: 'board', action: 'view' } },
      { name: 'PIO', href: '/pio', icon: PIOIcon, permission: { module: 'pio', action: 'view' } },
      { name: 'Community Lifelines', href: '/lifelines', icon: LifelinesIcon, requiresIncident: true, permission: { module: 'lifelines', action: 'view' } },
    ],
  },
  {
    name: 'Situational Awareness',
    icon: EyeIcon,
    defaultOpen: false,
    items: [
      { name: 'Weather Dashboard', href: '/weather', icon: WeatherIcon },
      { name: 'Flood Operations', href: '/flood-operations', icon: FloodOpsIcon, permission: { module: 'flood_ops', action: 'view' } },
      { name: 'Tropical', href: '/tropical', icon: TropicalIcon, permission: { module: 'map', action: 'view' } },
      { name: 'Damage Assessments', href: '/damage-assessments', icon: DamageAssessmentIcon, requiresIncident: true, permission: { module: 'damage_assessments', action: 'view' } },
      { name: 'Debris Estimates', href: '/debris', icon: DebrisIcon, requiresIncident: true, permission: { module: 'debris', action: 'view' } },
      { name: 'Critical Infrastructure', href: '/infrastructure/critical', icon: CriticalInfraIcon, permission: { module: 'infrastructure', action: 'view' } },
      { name: 'Road Closures', href: '/infrastructure/road-closures', icon: RoadIcon, permission: { module: 'infrastructure', action: 'view' } },
      { name: 'Utility Outages', href: '/infrastructure/utility-outages', icon: UtilityIcon, permission: { module: 'infrastructure', action: 'view' } },
      { name: 'HAZMAT', href: '/hazmat', icon: HazmatIcon, requiresIncident: true, featureFlag: 'hazmat_module' },
      { name: 'Community Reports', href: '/community-reports/submissions', icon: UserGroupIcon, requiresIncident: true, permission: { module: 'community_reports', action: 'view' } },
    ],
  },
  {
    name: 'People & Credentials',
    icon: UserGroupIcon,
    defaultOpen: false,
    items: [
      { name: 'Credentials', href: '/credentials', icon: CredentialIcon },
      { name: 'Credential Review', href: '/credentials/review', icon: TaskIcon },
      { name: 'Contacts', href: '/contacts', icon: ContactsIcon, permission: { module: 'contacts', action: 'view' } },
      { name: 'Agencies', href: '/agencies', icon: BuildingOfficeIcon, permission: { module: 'contacts', action: 'view' } },
      { name: 'Personnel Tracking', href: '/personnel-tracking', icon: SignalIcon, permission: { module: 'personnel_tracking', action: 'view' } },
      { name: 'Accountability (PAR)', href: '/safety/accountability', icon: ClipboardDocumentCheckIcon, requiresIncident: true, permission: { module: 'personnel_tracking', action: 'view' } },
      { name: 'Mutual-Aid Deployments', href: '/deployments', icon: HandshakeIcon, permission: { module: 'team', action: 'view' } },
      { name: 'At Risk Registry', href: '/at-risk-registry', icon: AtRiskIcon, permission: { module: 'at-risk-registry', action: 'view' } },
    ],
  },
  {
    name: 'Shelters & Evacuation',
    icon: ShelterIcon,
    defaultOpen: false,
    items: [
      { name: 'Shelters', href: '/shelters', icon: ShelterIcon, permission: { module: 'shelters', action: 'view' } },
      { name: 'Points of Distribution', href: '/pods', icon: BoxOpenIcon, requiresIncident: true, permission: { module: 'pods', action: 'view' } },
      { name: 'Evacuation Zones', href: '/evacuation', icon: EvacuationIcon, permission: { module: 'evacuation', action: 'view' } },
    ],
  },
  {
    name: 'Logistics',
    icon: TruckIcon,
    defaultOpen: false,
    items: [
      { name: 'Inventory', href: '/inventory', icon: InventoryIcon },
      { name: 'Resources', href: '/resources/pool', icon: ResourcesIcon, permission: { module: 'resources', action: 'view' } },
      { name: 'Facilities', href: '/facilities', icon: BuildingOfficeIcon, permission: { module: 'facilities', action: 'view' } },
      { name: 'Supply Requests', href: '/supply-requests', icon: TruckIcon, requiresIncident: true, permission: { module: 'supply_requests', action: 'view' } },
      { name: 'Approval Queue', href: '/supply-requests/dashboard', icon: ClipboardDocumentCheckIcon, requiresIncident: true, permission: { module: 'supply_requests', action: 'approve' } },
      { name: 'Procurement', href: '/procurement', icon: FileInvoiceDollarIcon, permission: { module: 'procurement', action: 'view' } },
      { name: 'Mutual Aid', href: '/mutual-aid', icon: HandshakeIcon, permission: { module: 'mutual_aid', action: 'view' }, featureFlag: 'partnerships' },
      { name: 'File Library', href: '/files', icon: FileLibraryIcon, permission: { module: 'file_library', action: 'view' } },
    ],
  },
  {
    name: 'Recovery & Planning',
    icon: ClipboardDocumentListIcon,
    defaultOpen: false,
    items: [
      { name: 'COOP Plans', href: '/coop/plans', icon: CoopIcon },
      { name: 'Business Continuity', href: '/business-continuity', icon: BuildingOfficeIcon, permission: { module: 'business_continuity', action: 'view' } },
      { name: 'Exercises', href: '/exercises', icon: ExerciseIcon, permission: { module: 'exercises', action: 'view' } },
      { name: 'After Action Reviews', href: '/after-action-reviews', icon: AARIcon, requiresIncident: true },
      { name: 'Recovery Dashboard', href: '/recovery', icon: ChartLineIcon, requiresIncident: true, permission: { module: 'recovery_dashboard', action: 'view' } },
      { name: 'FEMA Reimbursement', href: '/fema-reimbursement', icon: FileInvoiceDollarIcon, requiresIncident: true, permission: { module: 'fema_reimbursement', action: 'view' } },
      { name: 'Time Unit', href: '/time-unit', icon: ClockIcon, requiresIncident: true, permission: { module: 'time_unit', action: 'view' } },
      { name: 'Equipment Unit', href: '/equipment-usage', icon: TruckIcon, requiresIncident: true, permission: { module: 'equipment_unit', action: 'view' } },
      { name: 'Compensation/Claims', href: '/claims', icon: FileInvoiceDollarIcon, requiresIncident: true, permission: { module: 'comp_claims', action: 'view' } },
      { name: 'Reports', href: '/reports', icon: ChartBarIcon, permission: { module: 'reports', action: 'view' } },
    ],
  },
  {
    name: 'Wildfire',
    icon: FireIcon,
    defaultOpen: false,
    items: [
      { name: 'Fire Perimeters', href: '/wildfire', icon: FireIcon, permission: { module: 'wildfire', action: 'view' }, featureFlag: 'wildfire_module' },
      { name: 'Fire Behavior', href: '/fire-behavior', icon: ChartBarIcon, permission: { module: 'wildfire', action: 'view' }, featureFlag: 'wildfire_module' },
      { name: 'Smoke Tracking', href: '/smoke-tracking', icon: CloudIcon, permission: { module: 'wildfire', action: 'view' }, featureFlag: 'wildfire_module' },
    ],
  },
  {
    name: 'sUAS (Drones)',
    icon: DroneIcon,
    defaultOpen: false,
    items: [
      { name: 'Command Center', href: '/suas', icon: CommandCenterIcon, subGroup: 'Live Operations', permission: { module: 'suas', action: 'view' } },
      { name: 'Live Feeds', href: '/suas/fleet/livestream', icon: VideoCameraIcon, subGroup: 'Live Operations', permission: { module: 'suas', action: 'view_live_feeds' } },
      { name: 'Missions', href: '/suas/missions', icon: MissionIcon, subGroup: 'Planning', permission: { module: 'suas', action: 'view' } },
      { name: 'Search Patterns', href: '/suas/fleet/search-patterns', icon: MapIcon, subGroup: 'Planning', permission: { module: 'suas', action: 'plan_mission' } },
      { name: 'Aircraft', href: '/suas/drones', icon: DroneIcon, subGroup: 'Fleet & People', permission: { module: 'suas', action: 'view' } },
      { name: 'Pilots', href: '/suas/pilots', icon: UserGroupIcon, subGroup: 'Fleet & People', permission: { module: 'suas', action: 'view' } },
      { name: 'Operators', href: '/suas/operators', icon: CredentialIcon, subGroup: 'Fleet & People', permission: { module: 'suas', action: 'manage_operators' } },
      { name: 'Compliance', href: '/suas/compliance', icon: ClipboardDocumentCheckIcon, subGroup: 'Records & Compliance', permission: { module: 'suas', action: 'view' } },
      { name: 'Flight Logs', href: '/suas/flight-logs', icon: DocumentTextIcon, subGroup: 'Records & Compliance', permission: { module: 'suas', action: 'view' } },
      { name: 'Analytics', href: '/suas/analytics', icon: ChartBarIcon, subGroup: 'Records & Compliance', permission: { module: 'suas', action: 'view_analytics' } },
    ],
  },
  {
    name: 'Notifications & Automation',
    icon: BellAlertIcon,
    defaultOpen: false,
    items: [
      { name: 'Notifications', href: '/notifications', icon: NotificationIcon, permission: { module: 'notifications', action: 'view' } },
      { name: 'IPAWS/WEA Alerts', href: '/ipaws/alerts', icon: AlertIcon, permission: { module: 'ipaws', action: 'view' } },
      { name: 'Automations', href: '/automations', icon: AutomationIcon },
      { name: 'Businesses', href: '/businesses', icon: BusinessIcon, permission: { module: 'businesses', action: 'view' } },
    ],
  },
];

// =============================================================================
// COMPONENTS
// =============================================================================

interface NavGroupComponentProps {
  group: NavGroup;
  isOpen: boolean;
  onToggle: () => void;
  onNavClick: () => void;
  isDailyOps: boolean;
  hasFeature: (flag: string) => boolean;
}

function NavGroupComponent({ group, isOpen, onToggle, onNavClick, isDailyOps, hasFeature }: NavGroupComponentProps) {
  const regionRef = useRef<HTMLDivElement>(null);
  // The sidebar renders twice (mobile drawer + desktop rail), so a slug of the
  // group name would put duplicate ids in the document and aria-controls would
  // point at whichever one came first.
  const regionId = `${useId()}-nav-group`;

  // -----------------------------------------------------------------------
  // A collapsed group is hidden with `max-h-0 opacity-0 overflow-hidden` so it
  // can animate. That hides it from the eye and from nobody else: measured live
  // on 2026-08-26, seven collapsed groups were holding **33 links that were
  // still in the tab order and still in the accessibility tree**. A keyboard
  // user tabbing out of "Response" walked into an invisible menu -- focus ring
  // nowhere on screen, Enter navigating away to a page they never saw named --
  // and a screen-reader user heard every collapsed group's contents as though
  // it were open, which makes collapsing the sidebar useless to them.
  //
  // Same bug, same fix as BulkActionsToolbar: `inert`, set imperatively because
  // React 18 does not pass the attribute through. It removes the subtree from
  // focus order, hit-testing and AT while leaving the CSS transition intact.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const node = regionRef.current;
    if (!node) return;
    if (isOpen) {
      node.removeAttribute('inert');
    } else {
      node.setAttribute('inert', '');
    }
  }, [isOpen]);

  // Filter items based on permissions and plan entitlements; incident-dependent
  // items are shown disabled. Items without a featureFlag are always visible.
  const visibleItems = group.items.filter(item => {
    if (item.permission && !hasPermission(item.permission.module, item.permission.action)) return false;
    if (item.featureFlag && !hasFeature(item.featureFlag)) return false;
    return true;
  });

  // Don't render the group if no items are visible
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <div className="space-y-0.5">
      <button
        type="button"
        onClick={onToggle}
        // Open/closed was conveyed by a rotated chevron and nothing else -- a
        // shape, which is not a state. The sibling "More..." toggle in this same
        // file already declares aria-expanded; the nine group headers did not.
        aria-expanded={isOpen}
        aria-controls={regionId}
        className="group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-xs font-bold uppercase tracking-wider text-gray-300 hover:bg-white/[0.06] hover:text-white"
      >
        <group.icon className="h-4 w-4 shrink-0 text-gray-500 group-hover:text-gray-400 transition-colors duration-150" />
        <span className="flex-1 text-left truncate">{group.name}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ease-out ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        id={regionId}
        ref={regionRef}
        className={`overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <ul className="ml-4 space-y-0.5 border-l border-white/[0.06] pl-3">
          {visibleItems.map((item, index) => {
            const prevSubGroup = index > 0 ? visibleItems[index - 1].subGroup : undefined;
            const showSubGroupLabel = item.subGroup && item.subGroup !== prevSubGroup;
            const isDisabled = isDailyOps && item.requiresIncident;

            return (
              <li key={item.name}>
                {showSubGroupLabel && (
                  <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-widest text-theme-secondary">
                    {item.subGroup}
                  </div>
                )}
                {/*
                  * One muting mechanism, not two.
                  *
                  * `text-gray-600` on the sidebar's #111827 ground is 2.35:1,
                  * and the label carried `opacity-40` on top of that -- about
                  * 1.3:1, an unreadable smudge. The item's own name is the
                  * only thing saying WHICH module needs an active incident;
                  * the `title` tooltip that explains it does not exist on a
                  * touch screen, which is where field users are.
                  *
                  * gray-500 reads at 3.67:1 -- clearly dimmer than an enabled
                  * item's gray-400 (7:1), and still legible. The icon keeps
                  * its opacity so the row still looks switched off.
                  */}
                {isDisabled ? (
                  <span
                    className="group relative flex items-center gap-x-3 rounded-md px-2 py-1.5 text-sm font-medium text-gray-500 cursor-not-allowed select-none"
                    title="Requires active incident"
                  >
                    <item.icon className="h-5 w-5 shrink-0 opacity-40" />
                    <span>{item.name}</span>
                  </span>
                ) : (
                  <NavLink
                    to={item.href}
                    onClick={onNavClick}
                    end={visibleItems.some(other => other !== item && other.href.startsWith(item.href + '/'))}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-x-3 rounded-md px-2 py-1.5 text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-primary-500/[0.12] text-primary-300 before:absolute before:left-[-13px] before:top-1 before:bottom-1 before:w-[3px] before:rounded-full before:bg-primary-400'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 hover:translate-x-0.5'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.name}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// =============================================================================
// SIDEBAR
// =============================================================================

interface SidebarProps {
  onNavigate?: () => void;
}

export default function Sidebar({ onNavigate }: SidebarProps) {
  const { user, logout, permissions } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { isDailyOps, activeIncident } = useIncidentStore();
  const { logoUrl, appName } = useBrandingStore();
  const { hasFeature } = useEntitlements();
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const { data: myPosition = null } = useMyCurrentPosition(activeIncident?.id);
  const { open: openShortcutsModal } = useKeyboardShortcutsModal();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();

  // More... popover state
  const [moreOpen, setMoreOpen] = useState(false);
  const morePopoverRef = useRef<HTMLDivElement>(null);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);

  // Close popover on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (morePopoverRef.current && !morePopoverRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  // Keyboard contract: Escape closes and returns focus to the trigger;
  // ArrowUp/Down rove through menu items with wrap; Home/End jump.
  const closeMorePopover = useCallback(() => setMoreOpen(false), []);
  useMenuKeyboardNav(moreOpen, morePopoverRef, moreTriggerRef, closeMorePopover);

  const location = useLocation();

  // Initialize open groups based on defaultOpen and current route
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    const pathname = window.location.pathname;
    navGroups.forEach((group) => {
      // Auto-expand any group that contains the currently active route
      const containsActiveRoute = group.items.some(item =>
        pathname === item.href || pathname.startsWith(item.href + '/')
      );
      initial[group.name] = containsActiveRoute || (group.defaultOpen ?? false);
    });
    return initial;
  });

  // Auto-expand sections when navigating to a new route (e.g. via direct URL change)
  useEffect(() => {
    const pathname = location.pathname;
    setOpenGroups(prev => {
      const next = { ...prev };
      let changed = false;
      navGroups.forEach(group => {
        const containsActiveRoute = group.items.some(item =>
          pathname === item.href || pathname.startsWith(item.href + '/')
        );
        if (containsActiveRoute && !prev[group.name]) {
          next[group.name] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [location.pathname]);

  // Filter nav groups to exclude those with no visible items (permission-based only)
  const visibleNavGroups = useMemo(() => {
    return navGroups.filter(group => {
      const hasVisibleItems = group.items.some(item => {
        if (item.permission && !hasPermission(item.permission.module, item.permission.action)) return false;
        if (item.featureFlag && !hasFeature(item.featureFlag)) return false;
        return true;
      });
      return hasVisibleItems;
    });
  }, [isDailyOps, permissions, hasFeature]);

  const handleLogout = async () => {
    if (isEmbeddedMode()) {
      // Embedded in the native app (R4-CONSOLE): end THIS console session on
      // the server (never the device session), then sign out locally. The
      // session bridge tells the shell, which closes the console; no /login
      // page inside the WebView.
      await endEmbeddedConsoleSession();
      logout('user');
      return;
    }
    // P4-07: end THIS console's refresh family on the server (other consoles
    // and devices stay signed in), then clear the auth store and go to /login.
    await revokeConsoleSession();
    logout('user');
    window.location.href = '/login';
  };

  const handleNavClick = () => {
    onNavigate?.();
  };

  const toggleGroup = (groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  const ThemeIcon = theme === 'light' ? SunIcon : theme === 'dark' ? MoonIcon : ComputerIcon;
  const themeLabel = theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System';

  return (
    <div className="sidebar-chrome flex h-full w-64 flex-col bg-[var(--color-sidebar-dark)] shadow-[inset_-1px_0_0_0_rgba(255,255,255,0.04)]">
      {/* Branded header */}
      <div className="relative flex h-16 shrink-0 items-center px-6">
        <img
          className="h-8 w-auto"
          src={logoUrl || '/lightShield.png'}
          alt={appName}
          onError={(e) => {
            // Tenant logo failed (404/429/CORS) — fall back to the default Command
            // Bridge shield so the header doesn't show a broken image icon.
            const img = e.currentTarget;
            if (img.src.indexOf('/lightShield.png') === -1) {
              img.src = '/lightShield.png';
            }
          }}
        />
        <span className="ml-3 text-lg font-heading font-semibold tracking-tight text-white">{appName}</span>
        {/* Accent line */}
        <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-primary-500/40 via-primary-400/20 to-transparent" />
      </div>

      {/* Tenant Switcher */}
      <TenantSwitcher />

      {/* Incident Switcher */}
      <IncidentSwitcher />

      {/* Navigation */}
      <nav className="flex flex-1 flex-col px-4 py-4 overflow-y-auto scrollbar-hide">
        {/* Top-level items */}
        <ul className="space-y-0.5">
          {topNavigation.filter(item => !item.mobileOnly || isMobile).map((item) => {
            const isDisabled = isDailyOps && item.requiresIncident;
            return (
              <li key={item.name}>
                {isDisabled ? (
                  <span
                    className="group relative flex items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium text-gray-500 cursor-not-allowed select-none"
                    title="Requires active incident"
                  >
                    <item.icon className="h-5 w-5 shrink-0 opacity-40" />
                    <span>{item.name}</span>
                  </span>
                ) : (
                  <NavLink
                    to={item.href}
                    onClick={handleNavClick}
                    end={item.href === '/' || item.href === '/weather' || item.href === '/credentials' || item.href === '/infrastructure/critical'}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-primary-500/[0.12] text-primary-300 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary-400'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 hover:translate-x-0.5'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {item.name}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>

        {/* Grouped navigation */}
        <div className="mt-3 space-y-1 pt-3 border-t border-white/[0.06]">
          {visibleNavGroups.map((group) => (
            <NavGroupComponent
              key={group.name}
              group={group}
              isOpen={openGroups[group.name] ?? false}
              onToggle={() => toggleGroup(group.name)}
              onNavClick={handleNavClick}
              isDailyOps={isDailyOps}
              hasFeature={hasFeature}
            />
          ))}
        </div>

        <div className="flex-1" />

        {/* Bottom section */}
        <div className="mt-auto space-y-0.5 border-t border-white/[0.06] pt-4">
          {/* Admin Panel & Settings — hidden on desktop (shown in DesktopTopBar) */}
          {!isDesktop && user?.is_platform_admin && (
            <NavLink
              to="/admin"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `group relative flex items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-primary-500/[0.12] text-primary-300 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary-400'
                    : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 hover:translate-x-0.5'
                }`
              }
            >
              <AdminIcon className="h-5 w-5 shrink-0" />
              Admin Panel
            </NavLink>
          )}

          {!isDesktop && hasPermission('settings', 'view') && (
            <NavLink
              to="/settings"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `group relative flex items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-primary-500/[0.12] text-primary-300 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary-400'
                    : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200 hover:translate-x-0.5'
                }`
              }
            >
              <SettingsIcon className="h-5 w-5 shrink-0" />
              Settings
            </NavLink>
          )}

          {/* More... popover */}
          <div className="relative" ref={morePopoverRef}>
            <button
              ref={moreTriggerRef}
              type="button"
              onClick={() => setMoreOpen(!moreOpen)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              className={`group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium ${
                moreOpen
                  ? 'bg-white/[0.08] text-gray-200'
                  : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
              }`}
            >
              <EllipsisHorizontalIcon className="h-5 w-5 shrink-0" />
              More...
            </button>

            {moreOpen && (
              <div
                role="menu"
                aria-label="More options"
                className="absolute bottom-full left-0 mb-1 w-56 rounded-lg bg-[var(--color-sidebar-light)] shadow-xl ring-1 ring-white/[0.08] py-1 z-50"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    cycleTheme();
                    setMoreOpen(false);
                  }}
                  className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white"
                >
                  <ThemeIcon className="h-4 w-4 shrink-0" />
                  Theme: {themeLabel}
                </button>

                {hasPermission('audit', 'view') && (
                  <NavLink
                    to="/audit-logs"
                    role="menuitem"
                    onClick={() => {
                      handleNavClick();
                      setMoreOpen(false);
                    }}
                    className={({ isActive }) =>
                      `group flex items-center gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-primary-500/[0.12] text-primary-300'
                          : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                      }`
                    }
                  >
                    <AuditIcon className="h-4 w-4 shrink-0" />
                    Audit Logs
                  </NavLink>
                )}

                <NavLink
                  to="/help"
                  role="menuitem"
                  onClick={() => {
                    handleNavClick();
                    setMoreOpen(false);
                  }}
                  className={({ isActive }) =>
                    `group flex items-center gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? 'bg-primary-500/[0.12] text-primary-300'
                        : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                    }`
                  }
                >
                  <HelpIcon className="h-4 w-4 shrink-0" />
                  Help Center
                </NavLink>

                {hasPermission('support_tickets', 'view') && (
                  <NavLink
                    to="/support"
                    role="menuitem"
                    onClick={() => {
                      handleNavClick();
                      setMoreOpen(false);
                    }}
                    className={({ isActive }) =>
                      `group flex items-center gap-x-3 px-3 py-2 text-sm font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-primary-500/[0.12] text-primary-300'
                          : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                      }`
                    }
                  >
                    <HeadsetIcon className="h-4 w-4 shrink-0" />
                    Support
                  </NavLink>
                )}

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    openShortcutsModal();
                    handleNavClick();
                    setMoreOpen(false);
                  }}
                  className="group flex w-full items-center gap-x-3 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white"
                >
                  <KeyboardIcon className="h-4 w-4 shrink-0" />
                  Keyboard Shortcuts
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => void handleLogout()}
            className="group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-sm font-medium text-gray-400 hover:bg-white/[0.06] hover:text-gray-200"
          >
            <LogoutIcon className="h-5 w-5 shrink-0" />
            Sign out
          </button>
        </div>
      </nav>

      {/* User info — hidden on desktop (shown in DesktopTopBar) */}
      {!isDesktop && (
        <div className="border-t border-white/[0.06] p-4">
          <div className="flex items-center gap-x-3 rounded-md p-1 -m-1">
            <NavLink to="/account" className="shrink-0 hover:opacity-80 transition-opacity duration-150">
              <Avatar src={user?.avatar_url} name={user?.name} size="md" className="ring-2 ring-white/[0.08]" />
            </NavLink>
            <div className="flex-1 min-w-0">
              <NavLink to="/account" className="hover:underline">
                <p className="text-sm font-medium text-white truncate" title={user?.name || 'User'}>{user?.name || 'User'}</p>
              </NavLink>
              {activeIncident && myPosition?.position_name ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-900/40 text-primary-300 truncate max-w-[140px]" title={myPosition.position_name}>
                    {myPosition.position_code && <span className="mr-1 font-semibold">{myPosition.position_code}</span>}
                    {myPosition.position_name}
                  </span>
                  <button
                    type="button"
                    aria-label="Switch position"
                    title="Switch position"
                    onClick={() => setShowSwitchModal(true)}
                    className="shrink-0 p-0.5 text-theme-tertiary hover:text-primary-400"
                  >
                    <FaIcon icon="arrows-rotate" className="h-3 w-3" />
                  </button>
                </div>
              ) : activeIncident ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-theme-tertiary">No role assigned</span>
                  <button
                    type="button"
                    onClick={() => setShowSwitchModal(true)}
                    className="text-[11px] font-medium text-primary-400 hover:text-primary-300"
                  >
                    Claim
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400 truncate" title={user?.email}>{user?.email}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Switch Position Modal — needed on mobile too */}
      {!isDesktop && activeIncident && (
        <SwitchPositionModal
          isOpen={showSwitchModal}
          onClose={() => setShowSwitchModal(false)}
          incidentId={activeIncident.id}
          currentPosition={myPosition?.position_name ? myPosition : null}
        />
      )}
    </div>
  );
}
